
import type { Design, Route } from './schema';

const M_PER_MILE = 1609.344;
const FT_PER_M = 3.28084;

export interface Totals {
  distance: number; // metres
  gain: number; // metres
  movingTime: number | null; // seconds, only when every route has it
  hasElevation: boolean;
}

export function totals(routes: Route[]): Totals {
  let distance = 0;
  let gain = 0;
  let time = 0;
  let allTimed = routes.length > 0;
  for (const r of routes) {
    distance += r.distance;
    gain += r.gain;
    if (r.movingTime == null) allTimed = false;
    else time += r.movingTime;
  }
  return { distance, gain, movingTime: allTimed ? time : null, hasElevation: routes.some((r) => r.profile != null) };
}

const fmtInt = (n: number) => Math.round(n).toLocaleString('en-GB');
// One decimal for short routes so a marathon reads 26.2, not 26.
const fmtDistance = (n: number) => (n < 100 ? (Math.round(n * 10) / 10).toLocaleString('en-GB') : fmtInt(n));

export function formatDuration(seconds: number) {
  const s = Math.round(seconds);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

/** The stats line printed above the title. */
export function statsLine(design: Pick<Design, 'routes' | 'text'>): string {
  const { statsMode, statsTime, stats } = design.text;
  if (statsMode === 'custom') return stats;
  if (!design.routes.length) return '';
  const t = totals(design.routes);
  const parts =
    statsMode === 'imperial'
      ? [`${fmtDistance(t.distance / M_PER_MILE)} MILES`]
      : [`${fmtDistance(t.distance / 1000)} KM`];
  if (t.hasElevation) {
    parts.push(statsMode === 'imperial' ? `${fmtInt(t.gain * FT_PER_M)} FEET ELEVATION` : `${fmtInt(t.gain)} METRES ELEVATION`);
  }
  if (statsTime && t.movingTime != null) parts.push(`${formatDuration(t.movingTime)} MOVING TIME`);
  return parts.join(' // ');
}

export const routeSummary = (r: Route) => {
  if (r.distance < 1000) return `${Math.round(r.distance)} m`;
  return `${fmtDistance(r.distance / 1000)} km · ${fmtDistance(r.distance / M_PER_MILE)} mi`;
};

/**
 * Elevation along the combined route for the profile: `n` samples evenly spaced by distance
 * across all the routes that have elevation, in order.
 */
export function elevationProfile(routes: Route[], n = 400): number[] | null {
  const parts = routes.filter((r) => r.profile && r.distance > 0) as Array<Route & { profile: number[] }>;
  const total = parts.reduce((d, r) => d + r.distance, 0);
  if (!parts.length || !(total > 0)) return null;
  const out: number[] = [];
  let k = 0;
  let start = 0;
  for (let i = 0; i < n; i++) {
    const target = (total * i) / (n - 1);
    while (k < parts.length - 1 && target > start + parts[k].distance) start += parts[k++].distance;
    const p = parts[k].profile;
    const pos = Math.max(0, Math.min(1, (target - start) / parts[k].distance)) * (p.length - 1);
    const lo = Math.floor(pos);
    const hi = Math.min(p.length - 1, lo + 1);
    out.push(p[lo] + (p[hi] - p[lo]) * (pos - lo));
  }
  return out;
}
